import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, Search, Lock, Download, Trash2, Filter } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const CATS = [
  { key: null, label: "All" },
  { key: "insurance", label: "Insurance" },
  { key: "financial", label: "Financial" },
  { key: "meetings", label: "Meetings" },
  { key: "by_laws", label: "By-laws" },
  { key: "certificates", label: "Certificates" },
  { key: "correspondence", label: "Correspondence" },
  { key: "other", label: "Other" },
];

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Documents() {
  const { user } = useAuth();
  const schemeId = getActiveSchemeId();
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);

  const reload = async () => {
    const params = new URLSearchParams();
    if (filter) params.set("category", filter);
    if (q) params.set("q", q);
    const [d, members] = await Promise.all([
      api.get(`/schemes/${schemeId}/documents?${params}`),
      api.get(`/schemes/${schemeId}/members`),
    ]);
    setDocs(d.data);
    const mine = members.data.find((m) => m.user_id === user?.user_id);
    setMe(mine || (user?.system_role === "platform_admin" ? { role: "chair" } : null));
  };

  useEffect(() => { reload().catch((e) => toast.error(apiError(e))); /* eslint-disable-next-line */ }, [filter, q]);

  const canUpload = me?.role === "chair" || me?.role === "committee";

  const download = async (doc) => {
    try {
      const res = await api.get(`/schemes/${schemeId}/documents/${doc.document_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? File retained for the 7-year retention period.`)) return;
    try { await api.delete(`/schemes/${schemeId}/documents/${doc.document_id}`); toast.success("Document deleted"); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="documents-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
              <FileText size={12} /> Documents
            </span>
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>Scheme documents</h1>
            <p className="text-sm" style={{ color: "var(--slate)" }}>
              Insurance schedules, minutes, certificates, by-laws — kept 7 years per AU strata record-keeping norms.
            </p>
          </div>
          {canUpload && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button style={{ background: "var(--blue)", color: "white" }} data-testid="doc-upload-btn"><Plus size={14} className="mr-1.5"/> Upload</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <UploadForm schemeId={schemeId} onDone={() => { setOpen(false); reload(); }} />
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--tagline-grey)" }}/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, filename, tags…" className="pl-9" data-testid="doc-search-input"/>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mb-5 flex-wrap" data-testid="doc-filter-chips">
          {CATS.map((c) => (
            <button key={c.key || "all"}
              onClick={() => setFilter(c.key)}
              data-testid={`doc-filter-${c.key || "all"}`}
              className="px-3 py-1.5 text-xs font-medium rounded-full border transition-colors"
              style={{
                borderColor: filter === c.key ? "var(--blue)" : "var(--border)",
                background: filter === c.key ? "var(--blue-light)" : "white",
                color: filter === c.key ? "var(--blue-dark)" : "var(--slate)",
              }}>
              {c.label}
            </button>
          ))}
        </div>

        {docs.length === 0 ? (
          <div className="rounded-xl border bg-white px-6 py-12 text-center" style={{ borderColor: "var(--border)" }} data-testid="doc-empty">
            <FileText size={28} className="mx-auto mb-3" style={{ color: "var(--tagline-grey)" }}/>
            <p className="text-sm" style={{ color: "var(--slate)" }}>No documents match.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="doc-list">
            {docs.map((d) => (
              <div key={d.document_id} className="rounded-xl border bg-white px-4 lg:px-5 py-3 flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid={`doc-row-${d.document_id}`}>
                <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "var(--blue-light)" }}>
                  <FileText size={16} style={{ color: "var(--blue-dark)" }}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate" style={{ color: "var(--navy)" }}>{d.title}</span>
                    {d.visibility === "committee_only" && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--alert-light)", color: "var(--alert)" }} data-testid={`doc-committee-badge-${d.document_id}`}>
                        <Lock size={9}/> Committee only
                      </span>
                    )}
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--slate-light)", color: "var(--slate)" }}>{d.category.replace("_", " ")}</span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--tagline-grey)" }}>
                    {d.filename} · {fmtBytes(d.size_bytes)} · uploaded {new Date(d.uploaded_at).toLocaleDateString()} by {d.uploaded_by_name || "—"}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`doc-download-${d.document_id}`}>
                  <Download size={12} className="mr-1.5"/> Download
                </Button>
                {canUpload && (
                  <Button size="sm" variant="ghost" onClick={() => del(d)} data-testid={`doc-delete-${d.document_id}`} style={{ color: "var(--alert)" }}>
                    <Trash2 size={12}/>
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function UploadForm({ schemeId, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("scheme");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a file first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", description);
    fd.append("category", category);
    fd.append("tags", tags);
    fd.append("visibility", visibility);
    try {
      await api.post(`/schemes/${schemeId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3 mt-2" data-testid="doc-upload-form">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>File (max 10 MB)</Label>
          <Input type="file" className="mt-1" onChange={(e) => setFile(e.target.files?.[0] || null)} required data-testid="doc-file-input"/>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Title</Label>
          <Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title-input"/>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Description</Label>
          <Textarea rows={2} className="mt-1" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="doc-desc-input"/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="doc-category-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {CATS.filter(c => c.key).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Visibility</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="mt-1" data-testid="doc-visibility-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="scheme">Scheme (all members)</SelectItem>
                <SelectItem value="committee_only">Committee only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Tags (comma-separated)</Label>
          <Input className="mt-1" value={tags} onChange={(e) => setTags(e.target.value)} data-testid="doc-tags-input"/>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} style={{ background: "var(--blue)", color: "white" }} data-testid="doc-upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </form>
    </>
  );
}
